import type { ComponentPublicInstance } from 'vue'
// @ts-expect-error virtual file
import { defineNuxtPlugin, isNuxtError, useRuntimeConfig } from '#imports'
import * as Sentry from '@sentry/browser'
import { vueIntegration } from '@sentry/vue'
import { watch } from 'vue'
import { useUserSession } from '../composables/userSession'
import { CONFIG_KEY } from '../config'

export default defineNuxtPlugin(async (nuxt) => {
  const { user } = useUserSession()
  const config = useRuntimeConfig().public[CONFIG_KEY].sentry

  if (config.dsn) {
    Sentry.init({
      dsn: config.dsn,
      sendDefaultPii: true,
      environment: config.environment || import.meta.env.MODE,
      defaultIntegrations: false,
      integrations: [
        Sentry.dedupeIntegration(),
        Sentry.breadcrumbsIntegration(),
        Sentry.browserSessionIntegration(),
        Sentry.httpClientIntegration(),
        Sentry.httpContextIntegration(),
        Sentry.contextLinesIntegration(),
      ],
    })

    watch(user, (newUser) => {
      if (newUser) {
        Sentry.setUser({
          id: newUser.id,
          email: newUser.email,
          username: `${newUser.firstName} ${newUser.lastName}`,
        })
        return
      }
      Sentry.setUser(null)
    }, {
      immediate: true,
    })

    function onError(options: {
      error: unknown
      instance?: ComponentPublicInstance | null
      info?: string
    }) {
      const { error, instance, info } = options

      const metadata: Record<string, unknown> = {
        info,
      }

      if (instance?.$props) {
        if (instance.$props !== false) {
          metadata.propsData = instance.$props
        }
      }

      Sentry.setContext('browserDetails', {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      })

      setTimeout(() => {
        Sentry.captureException(error, {
          captureContext: { contexts: { nuxt: metadata } },
          mechanism: { handled: false },
        })
      })
    }

    nuxt.hook('app:created', (vueApp) => {
      Sentry.addIntegration(
        vueIntegration({
          app: vueApp,
          attachErrorHandler: false,
        }),
      )
    })

    nuxt.hook('app:error', (error) => {
      if (isNuxtError(error)) {
        if (error.status >= 300 && error.status < 500) {
          return
        }
      }
      onError({ error })
    })

    nuxt.hook('vue:error', error => onError({ error }))
  }
})
